import { playUIClick } from "@/audio/SoundManager";
import type { MusicPlayer, PlaylistTrack } from "@/audio/MusicPlayer";

export class MusicPlayerPanel {
  private panel = document.getElementById("music-player-panel")!;
  private controlsEl = document.getElementById("music-controls")!;
  private nowPlayingEl = document.getElementById("music-now-playing")!;
  private trackListEl = document.getElementById("music-track-list")!;
  private dropZoneEl = document.getElementById("music-drop-zone")!;

  private player: MusicPlayer | null = null;
  private unsubscribePlayer: (() => void) | null = null;

  constructor() {
    this.setupDragDrop();
  }

  setMusicPlayer(player: MusicPlayer): void {
    this.unsubscribePlayer?.();
    this.player = player;
    this.unsubscribePlayer = player.addCallbacks({
      onTrackChange: () => this.render(),
      onPlayStateChange: () => this.render(),
      onEnded: () => this.render(),
    });
    this.render();
  }

  // ── Private: drag-and-drop ─────────────────────────────────────────

  private setupDragDrop(): void {
    this.panel.addEventListener("dragover", (e) => {
      e.preventDefault();
      this.panel.classList.add("drag-over");
    });

    this.panel.addEventListener("dragleave", (e) => {
      if (!this.panel.contains(e.relatedTarget as Node | null)) {
        this.panel.classList.remove("drag-over");
      }
    });

    this.panel.addEventListener("drop", (e) => {
      e.preventDefault();
      this.panel.classList.remove("drag-over");
      if (!this.player || !e.dataTransfer) return;

      const files = Array.from(e.dataTransfer.files).filter((f) => f.type === "audio/mpeg");
      if (files.length === 0) return;

      const wasEmpty = this.player.getPlaylist().length === 0;
      this.player.addFiles(files);
      if (wasEmpty) this.player.play(0);
      this.render();
    });
  }

  // ── Private: rendering ─────────────────────────────────────────────

  private render(): void {
    if (!this.player) return;
    const playlist = this.player.getPlaylist();
    const currentIndex = this.player.getCurrentIndex();
    const playing = this.player.isPlaying();

    this.renderControls(playing, playlist.length > 0);
    this.renderNowPlaying(playlist[currentIndex] ?? null);
    this.renderTrackList(playlist, currentIndex);
  }

  private renderControls(playing: boolean, hasContent: boolean): void {
    this.controlsEl.innerHTML = "";

    const btns: { icon: string; title: string; action: () => void; disabled?: boolean }[] = [
      { icon: "⏮", title: "Previous", action: () => { playUIClick(); this.player?.prev(); }, disabled: !hasContent },
      {
        icon: playing ? "⏸" : "▶",
        title: playing ? "Pause" : "Play",
        action: () => {
          playUIClick();
          if (playing) this.player?.pause();
          else this.player?.play();
        },
        disabled: !hasContent,
      },
      { icon: "⏹", title: "Stop", action: () => { playUIClick(); this.player?.stop(); }, disabled: !hasContent },
      { icon: "⏭", title: "Next", action: () => { playUIClick(); this.player?.next(); }, disabled: !hasContent },
    ];

    for (const btn of btns) {
      const el = document.createElement("button");
      el.className = "music-ctrl-btn";
      el.title = btn.title;
      el.textContent = btn.icon;
      el.disabled = btn.disabled ?? false;
      el.addEventListener("click", btn.action);
      this.controlsEl.appendChild(el);
    }
  }

  private renderNowPlaying(track: PlaylistTrack | null): void {
    if (track) {
      this.nowPlayingEl.textContent = `♫ ${track.name}`;
      this.nowPlayingEl.style.display = "block";
      this.dropZoneEl.style.display = "none";
    } else {
      this.nowPlayingEl.style.display = "none";
      this.dropZoneEl.style.display = "flex";
    }
  }

  private renderTrackList(playlist: PlaylistTrack[], currentIndex: number): void {
    this.trackListEl.innerHTML = "";
    if (playlist.length === 0) return;

    for (let i = 0; i < playlist.length; i++) {
      const track = playlist[i]!;
      const row = document.createElement("div");
      row.className = "music-track-row" + (i === currentIndex ? " current" : "");

      const name = document.createElement("span");
      name.className = "music-track-name";
      name.textContent = `${i + 1}. ${track.name}`;
      name.title = track.name;
      row.appendChild(name);

      const removeBtn = document.createElement("button");
      removeBtn.className = "music-track-remove";
      removeBtn.textContent = "✕";
      removeBtn.title = "Remove";
      const idx = i;
      removeBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        playUIClick();
        this.player?.removeTrack(idx);
        this.render();
      });
      row.appendChild(removeBtn);

      row.addEventListener("click", () => {
        playUIClick();
        this.player?.play(idx);
      });

      this.trackListEl.appendChild(row);
    }
  }
}
